import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { loadTownsList } from "../store/towns";
import { useDispatch } from "react-redux";
import { useI18n } from "../i18n/LanguageContext";

/**
 * CountrySelector
 * Props:
 *  - options: [{ code: 'kz', label: 'Kazakhstan', emoji: '🇰🇿' }, ...]
 *  - defaultCode: string (например 'kz')
 *  - onSelect: function(code) -> void
 *  - saveToLocal: boolean (по умолчанию true)
 */
const DEFAULT_OPTIONS = [
  { code: "kz", label: "Kazakhstan", emoji: "🇰🇿" },
  { code: "ru", label: "Russia", emoji: "🇷🇺" },
  // { code: "us", label: "USA", emoji: "🇺🇸" },
  // { code: "gb", label: "United Kingdom", emoji: "🇬🇧" },
];

export default function CountrySelector({
  options = options || DEFAULT_OPTIONS,
  defaultCode,
  onSelect,
  saveToLocal = false,
  className = "",
}) {
  const storageKey = "userCountry";
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selected, setSelected] = useState(defaultCode);

  const currentCountry = localStorage.getItem("userCountry");

  const dispatch = useDispatch();
  const { t } = useI18n();

  useEffect(() => {
    if (currentCountry) {
      dispatch(loadTownsList());
    }
  }, [currentCountry]);
  useEffect(() => {
    function onDocClick(e) {
      if (
        listRef.current &&
        !listRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    // сбрасываем подсветку, когда открываем список
    if (open) setHighlightIndex(-1);
  }, [open]);

  const handleToggle = () => setOpen((v) => !v);

  const handleChoose = (code) => {
    onSelect(code);
    setSelected(code);
    if (saveToLocal && typeof window !== "undefined") {
      localStorage.setItem(storageKey, code);
    }
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    // навигация по списку
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = highlightIndex >= 0 ? highlightIndex : 0;
      handleChoose(options[idx].code);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const selectedObj = options.find((o) => o.code === selected) || {};

  return (
    <div className={`relative w-full inline-block text-left ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="w-full inline-flex justify-between items-center gap-2 px-4 py-2 bg-white border rounded shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
      >
        <span className="text-xl">{selectedObj.emoji || "🌍"}</span>
        <span className="whitespace-nowrap">
          {selectedObj.label || selected || t("country.choose")}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transform transition-transform ${
            open ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.354a.75.75 0 011.14.98l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          className="absolute z-50 mt-2 w-full max-h-60 overflow-auto bg-white border rounded shadow-lg py-1 ring-1 ring-black/5 focus:outline-none"
          onKeyDown={handleKeyDown}
        >
          {options.map((opt, idx) => {
            const active = opt.code === selected;
            const highlighted = idx === highlightIndex;
            return (
              <li
                key={opt.code}
                role="option"
                aria-selected={active}
                onClick={() => handleChoose(opt.code)}
                onMouseEnter={() => setHighlightIndex(idx)}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer select-none
                  ${
                    active
                      ? "bg-indigo-100 text-green-700 font-semibold"
                      : highlighted
                      ? "bg-gray-100"
                      : "hover:bg-gray-50"
                  }
                `}
              >
                <span className="text-lg">{opt.emoji || "🏳️"}</span>
                <div className="flex-1">
                  <div className="text-sm">{opt.label}</div>
                  <div className="text-xs text-gray-400">{opt.code}</div>
                </div>
                {active && (
                  <svg
                    className="w-5 h-5 text-green-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 10-1.408-1.42l-7.1 7.37-2.5-2.6A1 1 0 104.5 10.3l3.5 3.645a1 1 0 001.436.035l7.268-7.69z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

CountrySelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      emoji: PropTypes.string,
    })
  ),
  defaultCode: PropTypes.string,
  onSelect: PropTypes.func,
  saveToLocal: PropTypes.bool,
  className: PropTypes.string,
};
